// Company DAO — data access for companies.

import { BaseDao, type SupabaseClient, type InvalidateFn } from '../base.ts';
import type { PostgrestError } from '@supabase/supabase-js';
import type { CompanyRow } from "@shared/types/company.ts";

export class CompanyDao extends BaseDao<CompanyRow> {
  protected readonly dependentResources = [
    'companies',
    'company_bank_accounts',
  ];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'companies', invalidate);
  }

  async getByNip(nip: string): Promise<CompanyRow | null> {
    const { data, error }: { data: CompanyRow | null; error: PostgrestError | null } = await this.sb
      .from('companies')
      .select('*')
      .eq('nip', nip)
      .maybeSingle();
    if (error) throw error;
    return data;
  }

  /** Fetch id + parent_company_id for given company IDs (for family expansion). */
  async getWithParent(ids: number[]): Promise<{ id: number; parent_company_id: number | null }[]> {
    if (ids.length === 0) return [];
    const { data, error } = await this.sb
      .from('companies')
      .select('id, parent_company_id')
      .in('id', ids);
    if (error) throw error;
    return (data as { id: number; parent_company_id: number | null }[]) ?? [];
  }

  /** Fetch active child companies of given parent IDs. */
  async getChildren(parentIds: number[]): Promise<{ id: number; parent_company_id: number | null }[]> {
    if (parentIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('companies')
      .select('id, parent_company_id')
      .in('parent_company_id', parentIds)
      .eq('is_deleted', false);
    if (error) throw error;
    return (data as { id: number; parent_company_id: number | null }[]) ?? [];
  }

  /** Fetch NIPs for given company IDs. */
  async getNips(companyIds: number[]): Promise<{ id: number; nip: string | null }[]> {
    if (companyIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('companies')
      .select('id, nip')
      .in('id', companyIds);
    if (error) throw error;
    return (data as { id: number; nip: string | null }[]) ?? [];
  }

  /**
   * Resolve `fakturownia_client_id` → company id for a set of FA client ids
   * (finding fa_import_echo — the FA-import pairing client-id fallback).
   * Chunked `.in()` at 200; empty input → empty Map. Rows with a null
   * `fakturownia_client_id` are skipped defensively; on a duplicate client id
   * the first-seen company wins.
   */
  async getIdsByFakturowniaClientIds(clientIds: number[]): Promise<Map<number, number>> {
    const out = new Map<number, number>();
    if (clientIds.length === 0) return out;
    for (let i = 0; i < clientIds.length; i += 200) {
      const chunk = clientIds.slice(i, i + 200);
      const { data, error } = await this.sb
        .from('companies')
        .select('id, fakturownia_client_id')
        .in('fakturownia_client_id', chunk);
      if (error) throw error;
      for (const row of (data ?? []) as { id: number; fakturownia_client_id: number | null }[]) {
        if (row.fakturownia_client_id == null) continue;
        if (!out.has(row.fakturownia_client_id)) out.set(row.fakturownia_client_id, row.id);
      }
    }
    return out;
  }

  // --- Sync support ---

  /** Fetch all active companies with fields needed for sync company resolution.
   *  Paginated: PostgREST caps responses at 1000 rows by default. Without
   *  paging, sync silently misses companies on tenants with >1000 active
   *  companies — every invoice referencing a missed company is skipped with
   *  "Nie znaleziono sprzedawcy/nabywcy", looking like an upstream data bug.
   *  Mirror of mbank-sync's `loadMatchableInvoices` paging pattern. */
  async getAllForSync(): Promise<Pick<CompanyRow, 'id' | 'short_name' | 'full_name' | 'nip' | 'fakturownia_client_id' | 'parent_company_id'>[]> {
    const PAGE_SIZE = 1000;
    const all: Pick<CompanyRow, 'id' | 'short_name' | 'full_name' | 'nip' | 'fakturownia_client_id' | 'parent_company_id'>[] = [];
    for (let offset = 0; ; offset += PAGE_SIZE) {
      const { data, error } = await this.sb
        .from('companies')
        .select('id, nip, short_name, full_name, fakturownia_client_id, parent_company_id')
        .eq('is_deleted', false)
        .order('id', { ascending: true })
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      all.push(...data);
      if (data.length < PAGE_SIZE) break;
    }
    return all;
  }

  /** Batch-create companies. Falls back to individual inserts on conflict (duplicate NIP). */
  async batchCreate(inserts: Partial<CompanyRow>[], opts?: import('../base.ts').MutationOptions): Promise<CompanyRow[]> {
    if (inserts.length === 0) return [];
    const { data, error } = await this.sb
      .from('companies')
      .insert(inserts as Record<string, unknown>[])
      .select('id, short_name, nip, fakturownia_client_id');
    if (error) {
      // Fallback: insert one by one, skip failures (duplicates)
      const results: CompanyRow[] = [];
      for (const ins of inserts) {
        const { data: row, error: err } = await this.sb
          .from('companies')
          .insert(ins as Record<string, unknown>)
          .select('id, short_name, nip, fakturownia_client_id')
          .single();
        if (row) results.push(row as CompanyRow);
        else if (err) console.warn(`[sync] company create failed: ${ins.short_name} — ${err.message}`);
      }
      this.handleInvalidation(opts);
      return results;
    }
    this.handleInvalidation(opts);
    return (data ?? []) as CompanyRow[];
  }

  /** Backfill fakturownia_client_id on a company (only if currently null). */
  async updateFakturowniaClientId(companyId: number, clientId: number): Promise<void> {
    const { error } = await this.sb
      .from('companies')
      .update({ fakturownia_client_id: clientId })
      .eq('id', companyId)
      .is('fakturownia_client_id', null);
    if (error) throw error;
  }

  // --- Bank accounts ---

  /** Fetch the own company (is_own_company = true). */
  async getOwnCompany(): Promise<CompanyRow | null> {
    const { data, error }: { data: CompanyRow | null; error: PostgrestError | null } = await this.sb
      .from('companies')
      .select('*')
      .eq('is_own_company', true)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data;
  }

  /** Fetch bank account numbers for a company (id + account_number only).
   * For currency-aware lookups (KSeF resolution), use getBankAccountsWithCurrency. */
  async getBankAccounts(companyId: number): Promise<
    // verification_status/white_list_status optional in the TYPE (older test
    // doubles resolve without them) but always selected at runtime.
    { id: number; account_number: string; verification_status?: string | null; white_list_status?: boolean | null }[]
  > {
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('id, account_number, verification_status, white_list_status')
      .eq('company_id', companyId);
    if (error) throw error;
    return (data ?? []) as {
      id: number;
      account_number: string;
      verification_status?: string | null;
      white_list_status?: boolean | null;
    }[];
  }

  /** Fetch bank accounts with currency + needs_client_info for KSeF resolution.
   * currency is nullable since migration extend_bank_accounts_needs_client_info.
   * verification_status + white_list_status added for the trust-model rollout
   * (migration bank_account_verification_status). is_vat_account added for the
   * KONTO VAT designation (migration company_bank_accounts_is_vat_account) —
   * marks the ONE account per company used for VAT-part transfers. */
  async getBankAccountsWithCurrency(companyId: number): Promise<{ id: number; account_number: string; currency: string | null; needs_client_info: boolean; verification_status: string; white_list_status: boolean | null; is_vat_account: boolean }[]> {
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('id, account_number, currency, needs_client_info, verification_status, white_list_status, is_vat_account')
      .eq('company_id', companyId);
    if (error) throw error;
    return (data ?? []) as { id: number; account_number: string; currency: string | null; needs_client_info: boolean; verification_status: string; white_list_status: boolean | null; is_vat_account: boolean }[];
  }

  /**
   * Registry rows for SEVERAL companies at once — the capture backfill's classification
   * input. Strictly read-only: it neither checks the MF whitelist nor writes a row, which
   * is what makes it safe to run across the whole invoice history in one pass.
   */
  async getBankAccountsForCompanies(
    companyIds: number[],
  ): Promise<Array<{ company_id: number; account_number: string; currency: string | null; is_vat_account: boolean }>> {
    if (companyIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('company_id, account_number, currency, is_vat_account')
      .in('company_id', companyIds);
    if (error) throw error;
    return (data ?? []) as Array<{ company_id: number; account_number: string; currency: string | null; is_vat_account: boolean }>;
  }

  /** Fetch default bank account for a company + currency. */
  async getDefaultBankAccount(companyId: number, currency: string): Promise<{ account_number: string; bank_name: string | null; swift_code: string | null; currency: string } | null> {
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('account_number, bank_name, swift_code, currency')
      .eq('company_id', companyId)
      .eq('currency', currency)
      .eq('is_default', true)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data as { account_number: string; bank_name: string | null; swift_code: string | null; currency: string } | null;
  }

  /** Fetch language code for a country. */
  async getCountryLanguage(countryCode: string): Promise<string> {
    const { data, error }: { data: { language_code: string | null } | null; error: PostgrestError | null } = await this.sb
      .from('countries')
      .select('language_code')
      .eq('code', countryCode)
      .maybeSingle();
    if (error) throw error;
    return data?.language_code || 'en';
  }

  /** Insert a bank account record.
   * currency is nullable (null when whitelisted but currency is unknown — needs_client_info=true).
   * verification_status / status_reason are optional pass-through fields for the
   * trust-model rollout (migration bank_account_verification_status) — no logic here. */
  async insertBankAccount(record: {
    company_id: number;
    currency: string | null;
    account_number: string;
    bank_name: string | null;
    is_default: boolean;
    white_list_status: boolean | null;
    needs_client_info?: boolean;
    verification_status?: string;
    status_reason?: string | null;
  }): Promise<void> {
    const { error } = await this.sb
      .from('company_bank_accounts')
      .insert(record);
    if (error) throw error;
  }

  /** Fill currency once from KSeF evidence — never overwrites a stored value. */
  async setBankAccountCurrencyIfNull(id: number, currency: string): Promise<void> {
    const { error } = await this.sb
      .from('company_bank_accounts')
      .update({ currency })
      .eq('id', id)
      .is('currency', null);
    if (error) throw error;
  }

  /** Update needs_client_info flag on a bank account by id. */
  async updateBankAccountNeedsClientInfo(id: number, currency: string | null, needsClientInfo: boolean): Promise<void> {
    const { error } = await this.sb
      .from('company_bank_accounts')
      .update({ needs_client_info: needsClientInfo, currency })
      .eq('id', id);
    if (error) throw error;
  }

  /** Persist the outcome of a whitelist RE-CHECK on an EXISTING row (id already
   * known — this is not for new accounts, see insertBankAccount for that path).
   * A null white_list_status was never actually confirmed; BankAccountResolver
   * re-runs the whitelist check for such rows and calls this to record the fresh
   * result. verification_status is included only when the caller wants to
   * upgrade it (e.g. 'unverified' -> 'whitelisted') — omitted otherwise so a
   * payment_verified/operator_verified row is never touched by this path. */
  async updateBankAccountWhitelistStatus(
    id: number,
    update: { white_list_status: boolean; status_reason: string | null; verification_status?: string },
  ): Promise<void> {
    const patch: Record<string, unknown> = {
      white_list_status: update.white_list_status,
      status_reason: update.status_reason,
    };
    if (update.verification_status) patch.verification_status = update.verification_status;
    const { error } = await this.sb
      .from('company_bank_accounts')
      .update(patch)
      .eq('id', id);
    if (error) throw error;
  }

  /** Flag/unflag the single per-company KONTO VAT account. Best-effort callers
   * (BankAccountResolver live-flagging) should wrap this in try/catch — a
   * partial unique index (one is_vat_account=true row per company) means a
   * racing writer can make this throw. */
  async updateBankAccountIsVatAccount(id: number, isVatAccount: boolean): Promise<void> {
    const { error } = await this.sb
      .from('company_bank_accounts')
      .update({ is_vat_account: isVatAccount })
      .eq('id', id);
    if (error) throw error;
  }
}
